/**
 * Meetlog: een verslag van het geluid IN HET GESPREK, alleen als je erom vraagt.
 *
 * WAAROM DIT BESTAAT. De meetpagina op /meet zei op 11 september 2026 dat
 * sporen stoppen en meteen afspelen luid geeft. Het gesprek deed op
 * 12 september iets anders: het eerste bericht na het binnenkomen bleef stil
 * en de spreekknop bleef op slot, en dat OOK zonder de wijziging van de avond
 * ervoor (2848de3, teruggedraaid met 6c60f15). De meetpagina neemt dus
 * ergens een andere weg dan het gesprek. Dan moet je in het gesprek meten,
 * niet op een pagina ernaast.
 *
 * WAT HET DOET. Elke plek die met geluid of microfoon te maken heeft, roept
 * noteer() aan met een korte regel. Staat `?meet=1` in het adres, dan komen
 * die regels met een tijdstempel in een vak onder het gesprek (Meetvak).
 * Staat het er niet, dan doet noteer() niets en kost het een vergelijking.
 *
 * ALLEEN NEDERLANDS, net als de meetpagina: gereedschap voor Giovanni, geen
 * scherm voor een gast. Een gast ziet dit vak nooit, want die krijgt de link
 * zonder `?meet=1`.
 */

export type Luisteraar = (regels: readonly string[]) => void;

export interface Meetlog {
  noteer: (regel: string) => void;
  regels: () => readonly string[];
  volg: (luisteraar: Luisteraar) => () => void;
}

/**
 * Staat het meten aan? `?meet=1`, ook tussen andere parameters.
 *
 * Alleen precies "1": een schakelaar die bij twijfel opengaat is geen
 * schakelaar. Zie dezelfde keuze bij BEHEER_OPEN in sessie 7.
 */
export function meetActief(zoekopdracht: string): boolean {
  const kaal = zoekopdracht.replace(/^\?/, '');
  return kaal.split('&').some((deel) => deel === 'meet=1');
}

/** Bouwt een log. Los van de browser, zodat hij in node te toetsen is. */
export function maakMeetlog(actief: boolean, klok: () => number = () => performance.now()): Meetlog {
  const regels: string[] = [];
  const luisteraars = new Set<Luisteraar>();
  const begin = klok();

  return {
    noteer(regel) {
      if (!actief) return;
      const seconden = ((klok() - begin) / 1000).toFixed(2).padStart(7, ' ');
      regels.push(`${seconden}s ${regel}`);
      for (const l of luisteraars) l(regels);
    },
    regels: () => regels,
    volg(luisteraar) {
      luisteraars.add(luisteraar);
      return () => {
        luisteraars.delete(luisteraar);
      };
    },
  };
}

const inBrowser = typeof window !== 'undefined';
const log = maakMeetlog(inBrowser && meetActief(window.location.search));

/** Staat het meten aan in DIT tabblad? */
export function meten(): boolean {
  return inBrowser && meetActief(window.location.search);
}

/** Een regel in het verslag. Doet niets zonder `?meet=1`. */
export function noteer(regel: string): void {
  log.noteer(regel);
}

export function meetRegels(): readonly string[] {
  return log.regels();
}

export function volgMeetlog(luisteraar: Luisteraar): () => void {
  return log.volg(luisteraar);
}

/** Een bericht-id kort genoeg voor een regel op een telefoon. */
export function kort(id: string): string {
  return id.slice(0, 6);
}

/** WebKit's navigator.audioSession, of '-' waar die niet bestaat. */
export function audioSessieType(): string {
  const kandidaat = (navigator as { audioSession?: { type?: unknown } }).audioSession;
  return typeof kandidaat?.type === 'string' ? kandidaat.type : '-';
}

/** De stand van een AudioContext in een regel: stand, klok, geluidssessie. */
export function contextStand(ctx: AudioContext | null): string {
  if (ctx === null) return 'geen context';
  return `${ctx.state} klok=${ctx.currentTime.toFixed(3)} sessie=${audioSessieType()}`;
}
